import { useState, useEffect } from 'react';
import {
    Loader2,
    Filter,
    Search as SearchIcon
} from 'lucide-react';
import { isClosingSoon } from '../services/samApi';
import { getContractors, seedContractors, SEED_CONTRACTORS } from '../services/contractors';
import { getSavedContractIds, saveContract, unsaveContract, SavedContract } from '../services/favorites';
import { useAuth } from '../contexts/AuthContext';
import { Contractor } from '../types';
import ContractModal from './ContractModal';
import { useOpportunities } from '../hooks/useOpportunities';
import { useQueryClient } from '@tanstack/react-query';

// Modular Components
import KpiOverview from './dashboard/KpiOverview';
import ContractFilters from './dashboard/ContractFilters';
import SearchResultsList from './dashboard/SearchResultsList';

// Set-aside options
const SET_ASIDE_OPTIONS = [
    { value: '', label: 'Any Set-Aside' },
    { value: 'SBA', label: 'Small Business' },
    { value: 'SDVOSB', label: 'Veteran-Owned' },
    { value: '8A', label: '8(a)' },
    { value: 'WOSB', label: 'Women-Owned' },
    { value: 'HZC', label: 'HUBZone' },
];

// Cache key for localStorage
const CACHE_KEY = 'mh_search_results';
const CACHE_FILTERS_KEY = 'mh_search_filters';

export default function DiscoverySearch() {
    const queryClient = useQueryClient();
    const { user, appUser, isSuperAdmin } = useAuth();
    const [contractors, setContractors] = useState<Contractor[]>([]);
    const [selectedContractor, setSelectedContractor] = useState<string>('');

    // Filter State
    const [keyword, setKeyword] = useState('');
    const [naicsCode, setNaicsCode] = useState('');
    const [setAside, setSetAside] = useState('');
    const [postedFrom, setPostedFrom] = useState('');
    const [limit, setLimit] = useState('25');
    const [zipCode, setZipCode] = useState('');
    const [radius, setRadius] = useState('50');
    const [city, setCity] = useState('');
    const [stateCode, setStateCode] = useState('');

    // Search Trigger State
    const [searchParams, setSearchParams] = useState<any>(null);

    const [savedIds, setSavedIds] = useState<Set<string>>(new Set());
    const [_savingId, setSavingId] = useState<string | null>(null);
    const [selectedContract, setSelectedContract] = useState<any | null>(null);
    const [verifiedOnly, setVerifiedOnly] = useState(true);
    const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);

    // Discovery Source Toggle
    const [source, setSource] = useState<'sam' | 'cameron'>('sam');
    const [isSemantic, setIsSemantic] = useState(false);

    const currentContractor = contractors.find(c => c.id === selectedContractor) || null;

    // React Query Hook
    // React Query Hook
    const {
        data: results = [],
        isLoading: loading,
    } = useOpportunities(searchParams, selectedContractor, contractors, !!searchParams);

    // Local error state
    const [, setLocalError] = useState('');

    useEffect(() => {
        loadContractors();
        loadCachedData();
        loadSavedIds();
    }, []);

    useEffect(() => {
        if (!isSuperAdmin && appUser?.contractorId) {
            setSelectedContractor(appUser.contractorId);
        }
    }, [isSuperAdmin, appUser]);

    const loadCachedData = () => {
        try {
            const cachedFilters = localStorage.getItem(CACHE_FILTERS_KEY);

            if (cachedFilters) {
                const filters = JSON.parse(cachedFilters);
                if (isSuperAdmin) setSelectedContractor(filters.selectedContractor || '');
                setKeyword(filters.keyword || '');
                setNaicsCode(filters.naicsCode || '');
                setSetAside(filters.setAside || '');
                setPostedFrom(filters.postedFrom || getDefaultDate());
                setLimit(filters.limit || '25');
                setZipCode(filters.zipCode || '');
                setRadius(filters.radius || '50');
                setCity(filters.city || '');
                setStateCode(filters.stateCode || '');
            } else {
                setPostedFrom(getDefaultDate());
            }
        } catch (err) {
            console.error('Error loading cache:', err);
            setPostedFrom(getDefaultDate());
        }
    };

    const saveToCache = (newResults: any[], filters: any) => {
        try {
            localStorage.setItem(CACHE_KEY, JSON.stringify(newResults));
            localStorage.setItem(CACHE_FILTERS_KEY, JSON.stringify(filters));
        } catch (err) {
            console.error('Error saving to cache:', err);
        }
    };

    const getDefaultDate = () => {
        const date = new Date();
        date.setDate(date.getDate() - 30);
        return date.toISOString().split('T')[0];
    };

    const loadContractors = async () => {
        try {
            await seedContractors();
            const data = await getContractors();
            setContractors(data.length > 0 ? data : SEED_CONTRACTORS);
        } catch (err) {
            console.error('Error loading contractors:', err);
            setContractors(SEED_CONTRACTORS);
        }
    };

    const loadSavedIds = async () => {
        if (!selectedContractor) {
            setSavedIds(new Set());
            return;
        }
        try {
            const ids = await getSavedContractIds(selectedContractor);
            setSavedIds(ids);
        } catch (err) {
            console.error('Error loading saved IDs:', err);
        }
    };

    useEffect(() => {
        loadSavedIds();
    }, [selectedContractor]);

    const handleSearch = () => {
        const params = {
            limit: parseInt(limit),
            postedFrom: postedFrom,
            title: keyword || undefined,
            naicsCode: naicsCode || undefined,
            typeOfSetAside: setAside || undefined,
            zipCode: zipCode || undefined,
            radius: radius || undefined,
            city: city || undefined,
            state: stateCode || undefined,
            isSemantic: isSemantic, // Captured at time of search
            source: source
        };

        saveToCache([], {
            selectedContractor, keyword, naicsCode, setAside, postedFrom, limit,
            zipCode, radius, city, stateCode
        });

        setSearchParams(params);
    };

    useEffect(() => {
        if (user && selectedContractor && results.length > 0) {
            const highValueTargets = results.filter((r: any) => (r.fitScore || 0) >= 85);
            highValueTargets.forEach(async (target: any) => {
                if (!savedIds.has(target.noticeId)) {
                    try {
                        const contract: SavedContract = {
                            id: target.noticeId,
                            contractorId: selectedContractor,
                            savedBy: user.uid,
                            savedAt: new Date().toISOString(),
                            title: target.title,
                            agency: target.agency || target.fullParentPathName?.split('.')[0],
                            solicitationNumber: target.solicitationNumber,
                            postedDate: target.postedDate,
                            responseDeadline: target.responseDeadline,
                            type: target.type,
                            naicsCode: target.naicsCode,
                            uiLink: target.uiLink || `https://sam.gov/opp/${target.noticeId}/view`,
                            status: 'new',
                            priority: 'high',
                            notes: 'Auto-added by AI Hunter (Score > 85)'
                        };
                        await saveContract(contract);
                        // Optimistically update savedIds to prevent double-save attempt
                        setSavedIds(prev => new Set([...prev, target.noticeId]));
                    } catch (err) {
                        console.error("Auto-add failed", err);
                    }
                }
            });
        }
    }, [results, user, selectedContractor]);


    const handleToggleSave = async (opp: any, e?: React.MouseEvent) => {
        if (e) e.stopPropagation();
        if (!selectedContractor || !user) {
            setLocalError('Please select a contractor first.');
            return;
        }

        setSavingId(opp.noticeId);
        const isSaved = savedIds.has(opp.noticeId);

        try {
            if (isSaved) {
                await unsaveContract(selectedContractor, opp.noticeId);
                setSavedIds(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(opp.noticeId);
                    return newSet;
                });
            } else {
                const contract: SavedContract = {
                    id: opp.noticeId,
                    contractorId: selectedContractor,
                    savedBy: user.uid,
                    savedAt: new Date().toISOString(),
                    title: opp.title,
                    agency: opp.agency || opp.fullParentPathName?.split('.')[0],
                    solicitationNumber: opp.solicitationNumber,
                    postedDate: opp.postedDate,
                    responseDeadline: opp.responseDeadline,
                    type: opp.type,
                    naicsCode: opp.naicsCode,
                    uiLink: opp.uiLink || `https://sam.gov/opp/${opp.noticeId}/view`,
                    status: 'new',
                    priority: 'medium'
                };
                await saveContract(contract);
                setSavedIds(prev => new Set([...prev, opp.noticeId]));
            }
        } catch (err: any) {
            console.error('Error toggling save:', err);
            setLocalError(err.message || 'Failed to toggle save');
        } finally {
            setSavingId(null);
        }
    };

    const handleContractUpdate = (updatedContract: any) => {
        queryClient.setQueryData(['opportunities', searchParams, selectedContractor], (old: any[] | undefined) => {
            if (!old) return [];
            return old.map(opp => opp.noticeId === updatedContract.noticeId ? { ...opp, ...updatedContract } : opp);
        });
    };

    const displayedResults = verifiedOnly ? results.filter(r => (r.fitScore || 0) >= 50) : results;
    const hiddenCount = results.length - displayedResults.length;
    const closingSoonCount = displayedResults.filter(r => r.responseDeadline && isClosingSoon(r.responseDeadline)).length;
    const highFitCount = displayedResults.filter(r => (r.fitScore || 0) >= 60).length;

    return (
        <div className="min-h-screen bg-[#050505] text-white font-sans selection:bg-blue-500/30">
            <div className="max-w-[1600px] mx-auto space-y-6 p-6 pb-20 fade-in">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-white/5 pb-6">
                    <div>
                        <h1 className="text-2xl font-bold tracking-tight text-white flex items-center gap-3">
                            Market Discovery
                            <span className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg p-0.5 ml-2">
                                <button
                                    onClick={() => setSource('sam')}
                                    className={`px-3 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider transition-all ${source === 'sam' ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/40' : 'text-gray-400 hover:text-gray-300'}`}
                                >
                                    Federal (SAM)
                                </button>
                                <button
                                    onClick={() => setSource('cameron')}
                                    className={`px-3 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider transition-all ${source === 'cameron' ? 'bg-amber-600 text-white shadow-lg shadow-amber-900/40' : 'text-gray-400 hover:text-gray-300'}`}
                                >
                                    Local (Cameron)
                                </button>
                            </span>

                            <span className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg p-0.5 ml-4">
                                <button
                                    onClick={() => setIsSemantic(false)}
                                    className={`px-3 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider transition-all ${!isSemantic ? 'bg-blue-600/50 text-white' : 'text-gray-500 hover:text-gray-300'}`}
                                >
                                    Keyword
                                </button>
                                <button
                                    onClick={() => setIsSemantic(true)}
                                    className={`px-3 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider transition-all ${isSemantic ? 'bg-purple-600 text-white' : 'text-gray-500 hover:text-gray-300'}`}
                                >
                                    Semantic AI
                                </button>
                            </span>
                            {/* <span className="px-2 py-0.5 rounded text-[10px] bg-blue-900/30 text-blue-400 border border-blue-800 font-mono tracking-widest uppercase">Live Scan</span> */}
                        </h1>
                        <p className="text-gray-500 text-sm mt-1">Explore and analyze active federal solicitations from SAM.gov</p>
                    </div>

                    <div className="flex items-center gap-3">
                        <button onClick={() => setIsMobileFiltersOpen(!isMobileFiltersOpen)} className="xl:hidden flex items-center gap-2 px-3 py-1.5 bg-white/5 hover:bg-white/10 text-gray-300 rounded-lg text-xs font-medium border border-white/10 transition-colors">
                            <Filter className="w-3.5 h-3.5" /> {isMobileFiltersOpen ? 'Hide Filters' : 'Filters'}
                        </button>
                        <button onClick={() => setVerifiedOnly(!verifiedOnly)} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${verifiedOnly ? 'bg-blue-900/20 border-blue-800 text-blue-400' : 'bg-white/5 border-white/10 text-gray-400'}`}>
                            {verifiedOnly ? 'Show Hidden' : 'Hide Low Scores'}
                        </button>
                        <button onClick={handleSearch} disabled={loading} className="flex items-center gap-2 px-4 py-2 bg-blue-700 hover:bg-blue-600 text-white rounded-lg text-sm font-medium transition-all shadow-lg shadow-blue-900/40">
                            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <SearchIcon className="w-4 h-4" />}
                            <span>{loading ? 'Scanning...' : 'Scan for Opportunities'}</span>
                        </button>
                    </div>
                </div>

                {/* KPI Overview */}
                {results.length > 0 && (
                    <KpiOverview
                        displayedResultsCount={displayedResults.length}
                        hiddenCount={hiddenCount}
                        highFitCount={highFitCount}
                        closingSoonCount={closingSoonCount}
                    />
                )}

                {/* Main Search Grid */}
                <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 relative">
                    <ContractFilters
                        isSuperAdmin={isSuperAdmin}
                        contractors={contractors}
                        selectedContractor={selectedContractor}
                        setSelectedContractor={setSelectedContractor}
                        keyword={keyword}
                        setKeyword={setKeyword}
                        naicsCode={naicsCode}
                        setNaicsCode={setNaicsCode}
                        setAside={setAside}
                        setSetAside={setSetAside}
                        zipCode={zipCode}
                        setZipCode={setZipCode}
                        radius={radius}
                        setRadius={setRadius}
                        city={city}
                        setCity={setCity}
                        stateCode={stateCode}
                        setStateCode={setStateCode}
                        limit={limit}
                        setLimit={setLimit}
                        loading={loading}
                        handleSearch={handleSearch}
                        isMobileFiltersOpen={isMobileFiltersOpen}
                        setAsideOptions={SET_ASIDE_OPTIONS}
                    />

                    <SearchResultsList
                        loading={loading}
                        resultsCount={results.length}
                        displayedResults={displayedResults}
                        savedIds={savedIds}
                        handleToggleSave={handleToggleSave}
                        setSelectedContract={setSelectedContract}
                        selectedContractor={selectedContractor}
                    />
                </div>

                {/* Contract Modal */}
                {selectedContract && (
                    <ContractModal
                        contract={selectedContract}
                        contractor={currentContractor}
                        contractorId={selectedContractor}
                        isSaved={savedIds.has(selectedContract.noticeId)}
                        onClose={() => setSelectedContract(null)}
                        onSaveToggle={(saved) => {
                            if (saved) setSavedIds(prev => new Set([...prev, selectedContract.noticeId]));
                            else setSavedIds(prev => { const n = new Set(prev); n.delete(selectedContract.noticeId); return n; });
                        }}
                        onUpdate={handleContractUpdate}
                    />
                )}
            </div>
        </div>
    );
}
